import React from 'react';
import { Query } from '@apollo/client/react/components';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { toGlobalId } from 'graphql-relay';
import queryString from 'query-string';
import { FormattedMessage } from 'react-intl';
import { NavLink, Link } from 'react-router-dom';
import { Route, Switch, Redirect } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';

import Helmet from 'helmet/helmet';
import Img from 'lib/images/image-view';
import Styled from 'styled';
import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import ScrollToTop from 'lib/scroll/scroll-to-top';
import LoadingProgress from 'lib/react-router/loading-progress';
import { Halfpage, ZoznamHalfpageTop, ZoznamHalfpageBottom } from 'lib/google/dfp/ad-units/halfpage';

import { Site } from 'four-nets/site';
import GroupNavigation from './group-navigation/group-navigation';
import ErrorPage from 'four-nets/errors/error-page';
import members from 'four-nets/icons/svg/icon_members.svg';
import Hashtags from 'four-nets/photoblog/hashtags/hashtags';
import LeftMenu from 'four-nets/photoblog/left-menu/left-menu';
import subSection from 'four-nets/icons/svg/icon_sub-section.svg';
import NotFound from 'four-nets/errors/404/not-found';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';

import GroupAddPanel from './group-add-panel/group-add-panel';
import RelatedGroups from './related-groups/related-groups';
import GroupMainButton from './group-main-button/group-main-button';

import GroupInfo from './group-info/group-info';
import GroupFeedLoader from './group-feed/group-feed-loader';
import GroupPhotos from './group-photos/group-photos';
import GroupMembers from './group-members/group-members';
import GroupSettings from './group-settings/group-settings';

import query, { GroupLoaderQuery, GroupLoaderQueryVariables } from './group-loader.graphql';
import { MemberRole, GroupPrivacy } from './group-loader.graphql.types';
import defaultStyles from './group.scss';
import RouteWithSearch from 'lib/react-router/route-with-search';

interface Params {
    id: string;
}

interface Search {
    search?: string;
    h?: string;
}

interface Props extends RouteComponentProps<Params> {
    styles: typeof defaultStyles;
    search: Search;
}

interface State {
    search?: string;
}

export enum Section {
    MEMBERS = 'members',
    PHOTOS = 'photos',
    DETAIL = 'detail',
    SETTINGS = 'settings',
}

class Group extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    constructor(props: Props) {
        super(props);
        const { search } = this.props;
        const { search: searchQS } = search;
        this.state = { search: searchQS };
    }

    setSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ search: event.target.value });
    };

    onSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.props.history.push({
            pathname: this.props.location.pathname,
            search: `?${queryString.stringify({
                search: this.state.search,
            })}`,
        });
    };

    renderHelmet = (name?: string | null) => (H: any) => {
        const { site } = this.context;

        return (
            <FormattedMessage id='GROUP'>
                {message =>
                    name ? (
                        <H>
                            <title>
                                {message} - {name} | {site.name}
                            </title>
                        </H>
                    ) : (
                        <H>
                            <title>
                                {message} | {site.name}
                            </title>
                        </H>
                    )
                }
            </FormattedMessage>
        );
    };

    render() {
        const { styles, match, search } = this.props;
        const {
            params: { id },
        } = match;
        const { search: searchQS, h } = search;

        const { site } = this.context;

        const globalId = toGlobalId('GroupNode', id);

        return (
            <React.Fragment>
                <ScrollToTop id={id} />
                <Query<GroupLoaderQuery, GroupLoaderQueryVariables>
                    query={query}
                    variables={{ id: globalId }}
                    fetchPolicy='cache-first'
                >
                    {({ data, loading, error }) => {
                        const isJoinedByViewer = data && data.group && data.group.isJoinedByViewer;
                        const viewerMemberRole = data && data.group && data.group.viewerMemberRole;
                        const privacyType = data && data.group && data.group.privacyType;

                        let displayAddPanel = true;
                        if (privacyType === GroupPrivacy.GROUP_PRIVACY_PRIVATE) {
                            if (
                                viewerMemberRole !== MemberRole.MEMBER_ROLE_CREATOR &&
                                viewerMemberRole !== MemberRole.MEMBER_ROLE_MODERATOR
                            ) {
                                displayAddPanel = false;
                            }
                        }

                        let notFound = false;
                        if (!loading && privacyType === GroupPrivacy.GROUP_PRIVACY_PRIVATE) {
                            if (viewerMemberRole === MemberRole.MEMBER_ROLE_NON_MEMBER) {
                                notFound = true;
                            }
                        }

                        const redirect = loading ? null : <Redirect to={`/group/${id}/detail/`} />;

                        return notFound ? (
                            <NotFound />
                        ) : (
                            <LoadingProgress loading={loading}>
                                <ErrorPage error={error}>
                                    <div className={styles.columns}>
                                        <Helmet render={this.renderHelmet(data && data.group && data.group.name)} />
                                        <div>
                                            <h1 className={styles.title}>
                                                <FormattedMessage id='GROUPS' />
                                            </h1>
                                            <LeftMenu>
                                                {data && data.group && !data.group.isJoinedByViewer ? (
                                                    <NavLink
                                                        to={`/group/${id}/`}
                                                        className={classnames(styles.navLink, styles.menuOpen)}
                                                        activeClassName={styles.active}
                                                        isActive={() => true}
                                                    >
                                                        <SVGIcon src={subSection} className={styles.navIcon} />
                                                        {data && data.group && data.group.name}
                                                    </NavLink>
                                                ) : null}
                                            </LeftMenu>
                                        </div>
                                        <div className={styles.column}>
                                            <div className={styles.coverPhoto}>
                                                <FormattedMessage id='GROUP_COVER_PHOTO'>
                                                    {msg => (
                                                        <Img
                                                            alt={`${
                                                                (data && data.group && data.group.name) || ''
                                                            } - ${msg}`}
                                                            aspectFill={true}
                                                            sizes={{ xs: 250, md: 1280 }}
                                                            src={
                                                                (data &&
                                                                    data.group &&
                                                                    data.group.coverPhoto &&
                                                                    data.group.coverPhoto.src) ||
                                                                ''
                                                            }
                                                            srcSet={
                                                                (data &&
                                                                    data.group &&
                                                                    data.group.coverPhoto &&
                                                                    data.group.coverPhoto.srcset) ||
                                                                ''
                                                            }
                                                        />
                                                    )}
                                                </FormattedMessage>
                                            </div>
                                            <div className={styles.coverItems}>
                                                <div className={styles.coverTitle}>
                                                    <h2 className={styles.groupName}>
                                                        {data && data.group && data.group.name}
                                                    </h2>
                                                    <Link
                                                        to={`/group/${id}/members/`}
                                                        className={styles.membersWrapper}
                                                    >
                                                        <SVGIcon src={members} className={styles.icon} />
                                                        <span className={styles.members}>
                                                            <FormattedMessage
                                                                id='FORUM_%(num)s_PRIVATE_THREAD_MEMBERS'
                                                                values={{
                                                                    num: data && data.group && data.group.membersCount,
                                                                }}
                                                            />
                                                        </span>
                                                    </Link>
                                                </div>
                                                <GroupMainButton
                                                    id={globalId}
                                                    isJoinedByViewer={Boolean(
                                                        data && data.group && data.group.isJoinedByViewer
                                                    )}
                                                    isSubscribedByViewer={Boolean(
                                                        data && data.group && data.group.isSubscribedByViewer
                                                    )}
                                                />
                                            </div>
                                            {data && data.group ? (
                                                <GroupNavigation {...(data && data.group)} id={id} />
                                            ) : null}
                                            <Switch>
                                                <Route
                                                    path={`/group/${id}/members`}
                                                    render={() => <GroupMembers id={globalId} />}
                                                />
                                                <Route
                                                    path={`/group/${id}/settings`}
                                                    render={() =>
                                                        viewerMemberRole === MemberRole.MEMBER_ROLE_CREATOR ||
                                                        viewerMemberRole === MemberRole.MEMBER_ROLE_MODERATOR ? (
                                                            <GroupSettings id={globalId} />
                                                        ) : (
                                                            redirect
                                                        )
                                                    }
                                                />
                                                <Route
                                                    path={`/group/${id}/photos`}
                                                    render={() =>
                                                        isJoinedByViewer ? <GroupPhotos id={globalId} /> : redirect
                                                    }
                                                />
                                                <Route
                                                    path={`/group/${id}/detail`}
                                                    render={() => <GroupInfo id={globalId} />}
                                                />
                                                <RouteWithSearch
                                                    path={`/group/${id}/:slug?/:imageId?`}
                                                    render={props =>
                                                        isJoinedByViewer ? (
                                                            <GroupFeedLoader id={globalId} h={h} {...props} />
                                                        ) : (
                                                            redirect
                                                        )
                                                    }
                                                />
                                                <Route component={NotFound} />
                                            </Switch>
                                        </div>
                                        <aside className={styles.rightColumn}>
                                            <Switch>
                                                <Route
                                                    exact
                                                    path={`/group/${id}/detail`}
                                                    render={() => <RelatedGroups id={globalId} />}
                                                />
                                                <Route
                                                    path={`/group/${id}`}
                                                    render={() => (
                                                        <React.Fragment>
                                                            <ViewerConsumer>
                                                                {({ viewer }) =>
                                                                    viewer && displayAddPanel ? (
                                                                        <GroupAddPanel
                                                                            groupId={globalId}
                                                                            privacyType={
                                                                                privacyType ||
                                                                                GroupPrivacy.GROUP_PRIVACY_PUBLIC
                                                                            }
                                                                        />
                                                                    ) : null
                                                                }
                                                            </ViewerConsumer>

                                                            {site.isSlovak && site.isWedding ? (
                                                                <ZoznamHalfpageTop wrapperStyle={{ paddingTop: 15 }} />
                                                            ) : (
                                                                <Halfpage
                                                                    wrapperStyle={{ paddingTop: 15 }}
                                                                    adUnitCode='halfpage-top'
                                                                />
                                                            )}
                                                            <Hashtags groupId={globalId} />
                                                            {site.isSlovak && site.isWedding ? (
                                                                <ZoznamHalfpageBottom
                                                                    wrapperStyle={{ paddingTop: 15 }}
                                                                />
                                                            ) : (
                                                                <Halfpage
                                                                    wrapperStyle={{ paddingTop: 15 }}
                                                                    adUnitCode='halfpage-bottom'
                                                                />
                                                            )}
                                                        </React.Fragment>
                                                    )}
                                                />
                                            </Switch>
                                        </aside>
                                    </div>
                                </ErrorPage>
                            </LoadingProgress>
                        );
                    }}
                </Query>
            </React.Fragment>
        );
    }
}

export default Styled(defaultStyles)(Group);
